import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useGlobalMutation, useGlobalQuery } from '@/state/query-client/hooks';
import { useToast } from '@/hooks/use-toast';
import {
  getMySurveySubmissions,
  getSurveyFormLookup,
  getSurveySubmissionById,
  submitSurvey,
} from '../services/submission.service';
import { AnswerValue, GetSurveySubmissionsParams } from '../types/submission.types';

export const useGetMySurveySubmissions = (params: GetSurveySubmissionsParams, enabled = true) => {
  return useGlobalQuery({
    queryKey: ['hr-my-survey-submissions', params],
    queryFn: () => getMySurveySubmissions(params),
    enabled,
    staleTime: 60 * 1000,
  });
};

export const useGetSurveySubmissionById = (itemId?: string) => {
  return useGlobalQuery({
    queryKey: ['hr-survey-submission', itemId],
    queryFn: () => getSurveySubmissionById(itemId as string),
    enabled: Boolean(itemId),
  });
};

export const useGetSurveyFormLookup = (formId?: string) => {
  return useGlobalQuery({
    queryKey: ['hr-survey-form-lookup', formId],
    queryFn: () => getSurveyFormLookup(formId as string),
    enabled: Boolean(formId),
  });
};

export const useSubmitSurvey = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation();

  return useGlobalMutation({
    mutationFn: (params: { itemId: string; answers: AnswerValue[] }) => submitSurvey(params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hr-my-survey-submissions'] });
      queryClient.invalidateQueries({ queryKey: ['hr-survey-submission'] });
      toast({ title: t('SURVEY_SUBMITTED') });
    },
  });
};
